import { WebPlugin } from '@capacitor/core';
import { AppleSocialLogin } from './apple-provider';
import { FacebookSocialLogin } from './facebook-provider';
import { GoogleSocialLogin } from './google-provider';
import { OAuth2SocialLogin } from './oauth2-provider';
import { TwitterSocialLogin } from './twitter-provider';
export class SocialLoginWeb extends WebPlugin {
    constructor() {
        super();
        this.googleProvider = new GoogleSocialLogin();
        this.appleProvider = new AppleSocialLogin();
        this.facebookProvider = new FacebookSocialLogin();
        this.twitterProvider = new TwitterSocialLogin();
        this.oauth2Provider = new OAuth2SocialLogin();
        // Set up listener for OAuth redirects if we have a pending OAuth flow
        if (localStorage.getItem(SocialLoginWeb.OAUTH_STATE_KEY)) {
            console.log('OAUTH_STATE_KEY found');
            this.handleOAuthRedirect().catch((error) => {
                console.error('Failed to finish OAuth redirect', error);
                window.close();
            });
        }
    }
    async handleOAuthRedirect() {
        var _a;
        const url = new URL(window.location.href);
        const stateRaw = localStorage.getItem(SocialLoginWeb.OAUTH_STATE_KEY);
        let provider = null;
        let state;
        let nonce;
        if (stateRaw) {
            try {
                const parsed = JSON.parse(stateRaw);
                provider = (_a = parsed.provider) !== null && _a !== void 0 ? _a : null;
                state = parsed.state;
                nonce = parsed.nonce;
            }
            catch (_b) {
                provider = stateRaw === 'true' ? 'google' : null;
            }
        }
        let result = null;
        switch (provider) {
            case 'twitter':
                result = await this.twitterProvider.handleOAuthRedirect(url, state);
                break;
            case 'oauth2':
                result = await this.oauth2Provider.handleOAuthRedirect(url, state);
                break;
            case 'google':
            default:
                result = this.googleProvider.handleOAuthRedirect(url);
                break;
        }
        if (!result) {
            return;
        }
        // Build the message to send
        let message;
        if ('error' in result) {
            const resolvedProvider = provider !== null && provider !== void 0 ? provider : null;
            message = {
                type: 'oauth-error',
                provider: resolvedProvider,
                error: result.error,
            };
        }
        else {
            message = Object.assign({ type: 'oauth-response', provider: result.provider }, result.result);
        }
        // Try postMessage first (works when window.opener is accessible)
        try {
            if (window.opener) {
                window.opener.postMessage(message, window.location.origin);
            }
        }
        catch (_c) {
            // Cross-origin error - window.opener may not be accessible
            console.log('postMessage to opener failed, using BroadcastChannel');
        }
        // Also use BroadcastChannel as a fallback (works across same-origin windows
        // even when window.opener is not accessible due to cross-origin navigation)
        try {
            // Determine the channel name based on provider and state/nonce
            let channelName = null;
            if (provider === 'oauth2' && state) {
                channelName = `oauth2_${state}`;
            }
            else if (provider === 'twitter' && state) {
                channelName = `twitter_oauth_${state}`;
            }
            else if (provider === 'google' && nonce) {
                channelName = `google_oauth_${nonce}`;
            }
            if (channelName) {
                const channel = new BroadcastChannel(channelName);
                channel.postMessage(message);
                channel.close();
            }
        }
        catch (_d) {
            // BroadcastChannel not supported or other error
            console.log('BroadcastChannel not available');
        }
        window.close();
    }
    async initialize(options) {
        var _a, _b, _c, _d;
        const initPromises = [];
        if ((_a = options.google) === null || _a === void 0 ? void 0 : _a.webClientId) {
            initPromises.push(this.googleProvider.initialize(options.google.webClientId, options.google.mode, options.google.hostedDomain, options.google.redirectUrl));
        }
        if ((_b = options.apple) === null || _b === void 0 ? void 0 : _b.clientId) {
            initPromises.push(this.appleProvider.initialize(options.apple.clientId, options.apple.redirectUrl, options.apple.useProperTokenExchange));
        }
        if ((_c = options.facebook) === null || _c === void 0 ? void 0 : _c.appId) {
            initPromises.push(this.facebookProvider.initialize(options.facebook.appId, options.facebook.locale));
        }
        if ((_d = options.twitter) === null || _d === void 0 ? void 0 : _d.clientId) {
            initPromises.push(this.twitterProvider.initialize(options.twitter.clientId, options.twitter.redirectUrl, options.twitter.defaultScopes, options.twitter.forceLogin, options.twitter.audience));
        }
        if (options.oauth2 && Object.keys(options.oauth2).length > 0) {
            initPromises.push(this.oauth2Provider.initializeProviders(options.oauth2));
        }
        await Promise.all(initPromises);
    }
    async login(options) {
        switch (options.provider) {
            case 'google':
                return this.googleProvider.login(options.options);
            case 'apple':
                return this.appleProvider.login(options.options);
            case 'facebook':
                return this.facebookProvider.login(options.options);
            case 'twitter':
                return this.twitterProvider.login(options.options);
            case 'oauth2':
                return this.oauth2Provider.login(options.options);
            default:
                throw new Error(`Login for ${options.provider} is not implemented on web`);
        }
    }
    async logout(options) {
        switch (options.provider) {
            case 'google':
                return this.googleProvider.logout();
            case 'apple':
                return this.appleProvider.logout();
            case 'facebook':
                return this.facebookProvider.logout();
            case 'twitter':
                return this.twitterProvider.logout();
            case 'oauth2':
                if (!options.providerId) {
                    throw new Error('providerId is required for oauth2 logout');
                }
                return this.oauth2Provider.logout(options.providerId);
            default:
                throw new Error(`Logout for ${options.provider} is not implemented`);
        }
    }
    async isLoggedIn(options) {
        switch (options.provider) {
            case 'google':
                return this.googleProvider.isLoggedIn();
            case 'apple':
                return this.appleProvider.isLoggedIn();
            case 'facebook':
                return this.facebookProvider.isLoggedIn();
            case 'twitter':
                return this.twitterProvider.isLoggedIn();
            case 'oauth2':
                if (!options.providerId) {
                    throw new Error('providerId is required for oauth2 isLoggedIn');
                }
                return this.oauth2Provider.isLoggedIn(options.providerId);
            default:
                throw new Error(`isLoggedIn for ${options.provider} is not implemented`);
        }
    }
    async getAuthorizationCode(options) {
        switch (options.provider) {
            case 'google':
                return this.googleProvider.getAuthorizationCode();
            case 'apple':
                return this.appleProvider.getAuthorizationCode();
            case 'facebook':
                return this.facebookProvider.getAuthorizationCode();
            case 'twitter':
                return this.twitterProvider.getAuthorizationCode();
            case 'oauth2':
                if (!options.providerId) {
                    throw new Error('providerId is required for oauth2 getAuthorizationCode');
                }
                return this.oauth2Provider.getAuthorizationCode(options.providerId);
            default:
                throw new Error(`getAuthorizationCode for ${options.provider} is not implemented`);
        }
    }
    async refresh(options) {
        switch (options.provider) {
            case 'google':
                return this.googleProvider.refresh();
            case 'apple':
                return this.appleProvider.refresh();
            case 'facebook':
                return this.facebookProvider.refresh(options.options);
            case 'twitter':
                return this.twitterProvider.refresh();
            case 'oauth2': {
                const oauth2Options = options.options;
                if (!(oauth2Options === null || oauth2Options === void 0 ? void 0 : oauth2Options.providerId)) {
                    throw new Error('providerId is required for oauth2 refresh');
                }
                return this.oauth2Provider.refresh(oauth2Options.providerId);
            }
            default:
                throw new Error(`Refresh for ${options.provider} is not implemented`);
        }
    }
    async providerSpecificCall(options) {
        throw new Error(`Provider specific call for ${options.call} is not implemented`);
    }
    async getPluginVersion() {
        return { version: 'web' };
    }
}
SocialLoginWeb.OAUTH_STATE_KEY = 'social_login_oauth_pending';
//# sourceMappingURL=web.js.map